/** @jsx jsx */
import { h, FunctionalComponent, Fragment } from 'preact'
import { useState, useEffect, useRef } from 'preact/hooks'
import { jsx } from '@emotion/core'
import { fetchPosts } from './groupOfLinksService'
import { Post } from './types'
import { useSiteVars } from '../../themes/useSiteVars'
import { getDefaultGroupOfLinksStylesObj, getExpStyles } from './groupOfLinks.styles'
import { useAnalytics } from './analytics/useAnalytics'
import { Link } from './components/Link'
import { Divider } from './components/Divider'
import { LinkWithImage } from './components/LinkWithImage'
import { SITE_VARIABLES } from '../../themes/variables'

const SWIMSUIT_SLUG = 'si_swimsuit'
interface GroupOfLinksProps {
    url: string
    embedName?: string
    withImages?: string
    experiments?: string[]
}

interface RenderCompProps {
    posts: Post[]
    onLinkClick: (url: string, index: number) => void
    siteVars: typeof SITE_VARIABLES
}

const getImageCropped = (imageUrl: string, width: number, height: number) => {
    const parts = imageUrl.split('/images/')

    if (parts.length < 2) {
        return imageUrl
    }

    return `https://images2.minutemediacdn.com/image/upload/c_auto,w_${width},h_${height}/images/${parts[1]}`
}

const GroupOfLinksExpV1: FunctionalComponent<RenderCompProps> = ({ posts, onLinkClick, siteVars }) => {
    const styles = getExpStyles(siteVars)
    return (
        <div>
            <div css={styles.header}>Trending Stories</div>
            <div>
                {posts.map((post, index) => (
                    <div key={post.id || index} css={styles.row}>
                        <a href={post.url} onClick={() => onLinkClick(post.url, index)} css={styles.link}>
                            <div css={styles.card}>
                                <img
                                    src={getImageCropped(post.image?.value?.url, 240, 240)}
                                    alt={post.image?.value?.alt}
                                    css={styles.image}
                                />
                                <div css={styles.textBlock}>{post.title}</div>
                            </div>
                        </a>
                    </div>
                ))}
            </div>
        </div>
    )
}

const GroupOfLinksExpV2: FunctionalComponent<RenderCompProps> = ({ posts, onLinkClick, siteVars }) => {
    const styles = getExpStyles(siteVars)
    return (
        <div>
            <div css={styles.header}>Trending Stories</div>
            <div>
                {posts.map((post, index) => (
                    <div key={post.id || index} css={styles.row} style={{ marginBottom: '15px' }}>
                        <a href={post.url} onClick={() => onLinkClick(post.url, index)} css={styles.link}>
                            <div css={styles.card} style={{ alignItems: 'center' }}>
                                <div>
                                    <div css={styles.number}>{index + 1}</div>
                                </div>
                                <div css={styles.textBlock}>{post.title}</div>
                            </div>
                        </a>
                    </div>
                ))}
            </div>
        </div>
    )
}

const GroupOfLinks: FunctionalComponent<GroupOfLinksProps> = ({
    url,
    embedName = '',
    withImages = 'false',
    experiments = [],
}) => {
    const siteVars = useSiteVars()
    const [posts, setPosts] = useState<Post[]>([])
    const [isSwimsuit, setIsSwimsuit] = useState(false)
    const [isSI, setIsSI] = useState(false)
    const shouldRenderWithImages = withImages === 'true'
    const groupOfLinksStyles = getDefaultGroupOfLinksStylesObj(siteVars, isSwimsuit, shouldRenderWithImages, isSI)
    const [currentArticleId, setCurrentArticleId] = useState('')
    const ref = useRef<HTMLDivElement>(null)
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const { onLinkClick } = useAnalytics(posts.length, ref.current!, embedName, shouldRenderWithImages)

    useEffect(() => {
        const getPosts = async () => {
            const { posts: postsData, property } = await fetchPosts(url)
            setPosts(postsData)
            setIsSwimsuit(property === SWIMSUIT_SLUG)
        }
        getPosts()
    }, [url])

    useEffect(() => {
        if (window) {
            ;(window.location.href.includes('si.com') ||
                window.location.href.includes('newsight-qa2.qa.minutesvc.com')) &&
                setIsSI(true)
        }
    }, [])

    useEffect(() => {
        window.mmClientApi = window.mmClientApi || []
        window.mmClientApi.push('getPageData', (postData: any) => {
            setCurrentArticleId(postData.articleId)
        })
    }, [])

    useEffect(() => {
        const articleIdFromEmbedElement = document.getElementById('embed')?.getAttribute('articleId')
        articleIdFromEmbedElement && setCurrentArticleId(articleIdFromEmbedElement)
    }, [])

    useEffect(() => {
        window.parent.postMessage(
            {
                sentinel: 'amp',
                type: 'embed-size',
                height: ref.current?.clientHeight ?? 0,
            },
            '*',
        )
    }, [posts])

    return (
        <div ref={ref} css={groupOfLinksStyles.wrapper}>
            {experiments.includes('gol-ui|design-v1') ? (
                <GroupOfLinksExpV1 posts={posts} onLinkClick={onLinkClick} siteVars={siteVars} />
            ) : experiments.includes('gol-ui|design-v2') ? (
                <GroupOfLinksExpV2 posts={posts} onLinkClick={onLinkClick} siteVars={siteVars} />
            ) : (
                <Fragment>
                    {shouldRenderWithImages && !isSwimsuit && <Divider styles={groupOfLinksStyles} />}
                    {posts.map((post, index) => {
                        return post.verified && currentArticleId !== post.id ? (
                            shouldRenderWithImages ? (
                                <LinkWithImage
                                    isSwimsuit={isSwimsuit}
                                    index={index}
                                    post={post}
                                    onLinkClick={onLinkClick}
                                    styles={groupOfLinksStyles}
                                />
                            ) : (
                                <Link index={index} post={post} onLinkClick={onLinkClick} styles={groupOfLinksStyles} />
                            )
                        ) : (
                            <Fragment></Fragment>
                        )
                    })}
                </Fragment>
            )}
        </div>
    )
}

export default GroupOfLinks
